import React from 'react';
import Link from 'next/link';
import type { JSONContent } from '@tiptap/core';
import { ImageWithFallback } from '../figma/ImageWithFallback';

const HASHTAG_RE = /(#[a-zA-Z][a-zA-Z0-9_]*)/g;

/** Splits plain text on #hashtag tokens and links each one to a search for
 * that term — hashtags are typed as ordinary text (see the editor's "Insert
 * hashtag" toolbar button), not a special mark, so this is purely a render-
 * time transform. */
function highlightHashtags(text: string, key: React.Key): React.ReactNode {
  const parts = text.split(HASHTAG_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith('#')
      ? (
        <Link
          key={`${key}-${i}`}
          href={`/search?q=${encodeURIComponent(part)}`}
          className="text-hustlance-primary hover:underline"
        >
          {part}
        </Link>
      )
      : <React.Fragment key={`${key}-${i}`}>{part}</React.Fragment>
  );
}

/**
 * Renders Tiptap's structured JSON content as real React elements — never
 * dangerouslySetInnerHTML, so there is no HTML-injection surface for article
 * bodies at all. Only needs to cover the node/mark types StarterKit +
 * Image + Link actually produce.
 */

function renderMarks(text: string, marks: JSONContent['marks'], key: React.Key): React.ReactNode {
  const hasCode = (marks ?? []).some(m => m.type === 'code');
  let node: React.ReactNode = hasCode ? text : highlightHashtags(text, key);
  for (const mark of marks ?? []) {
    switch (mark.type) {
      case 'bold':
        node = <strong key={key}>{node}</strong>;
        break;
      case 'italic':
        node = <em key={key}>{node}</em>;
        break;
      case 'code':
        node = <code key={key} className="bg-gray-100 text-gray-900 rounded px-1.5 py-0.5 text-[0.9em]">{node}</code>;
        break;
      case 'link':
        node = (
          <a
            key={key}
            href={mark.attrs?.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hustlance-primary underline underline-offset-2 hover:text-hustlance-secondary transition-colors"
          >
            {node}
          </a>
        );
        break;
    }
  }
  return node;
}

function renderInline(nodes: JSONContent[] | undefined): React.ReactNode {
  if (!nodes) return null;
  return nodes.map((n, i) => {
    if (n.type === 'text') return <React.Fragment key={i}>{renderMarks(n.text ?? '', n.marks, i)}</React.Fragment>;
    if (n.type === 'hardBreak') return <br key={i} />;
    return null;
  });
}

function renderNode(node: JSONContent, key: React.Key): React.ReactNode {
  switch (node.type) {
    case 'paragraph':
      return (
        <p key={key} className="text-[1.15rem] leading-[1.8] text-gray-800 mb-7">
          {renderInline(node.content)}
        </p>
      );
    case 'heading': {
      const level = node.attrs?.level ?? 2;
      const className = level === 1
        ? 'font-editorial text-3xl text-gray-900 mt-14 mb-5'
        : level === 2
        ? 'font-editorial text-2xl text-gray-900 mt-14 mb-5'
        : 'font-editorial text-xl text-gray-900 mt-10 mb-4';
      const Tag = (`h${Math.min(Math.max(level, 1), 3)}`) as 'h1' | 'h2' | 'h3';
      return <Tag key={key} className={className}>{renderInline(node.content)}</Tag>;
    }
    case 'bulletList':
      return (
        <ul key={key} className="list-disc pl-6 mb-7 space-y-3 text-[1.15rem] leading-[1.8] text-gray-800">
          {node.content?.map((li, i) => <li key={i}>{li.content?.map((c, j) => renderNode(c, j))}</li>)}
        </ul>
      );
    case 'orderedList':
      return (
        <ol key={key} className="list-decimal pl-6 mb-7 space-y-3 text-[1.15rem] leading-[1.8] text-gray-800">
          {node.content?.map((li, i) => <li key={i}>{li.content?.map((c, j) => renderNode(c, j))}</li>)}
        </ol>
      );
    case 'listItem':
      return <React.Fragment key={key}>{node.content?.map((c, i) => renderNode(c, i))}</React.Fragment>;
    case 'blockquote':
      return (
        <blockquote key={key} className="font-editorial border-l-2 border-gray-900 pl-8 py-1 my-10 text-2xl leading-snug italic text-gray-900">
          {node.content?.map((c, i) => renderNode(c, i))}
        </blockquote>
      );
    case 'codeBlock':
      return (
        <pre key={key} className="bg-gray-900 text-gray-100 rounded-xl p-5 mb-6 overflow-x-auto text-sm">
          <code>{node.content?.map(c => c.text).join('')}</code>
        </pre>
      );
    case 'horizontalRule':
      return <hr key={key} className="my-10 border-gray-200" />;
    case 'image': {
      const caption = node.attrs?.alt || node.attrs?.title;
      return (
        <figure key={key} className="my-8">
          <ImageWithFallback
            src={node.attrs?.src}
            alt={node.attrs?.alt || ''}
            className="w-full rounded-2xl object-cover"
          />
          {caption && <figcaption className="text-sm text-gray-500 text-center mt-3">{caption}</figcaption>}
        </figure>
      );
    }
    default:
      return null;
  }
}

export function TiptapRenderer({ content }: { content: JSONContent }) {
  if (!content?.content) return null;
  return <div>{content.content.map((node, i) => renderNode(node, i))}</div>;
}
